/**
 * EL DIFF DE UNA LÍNEA DE AUDITORÍA.
 *
 * `audit_log` guarda `before` y `after` de cada cambio, y `AuditEntryRead` los
 * devuelve desde siempre — **el frontend los venía tirando**. La tab Actividad
 * mostraba quién y cuándo, nunca de qué a qué, que es lo que uno va a buscar
 * cuando abre esa pantalla ("¿quién lo pasó a Culminado?" es media pregunta;
 * "¿desde dónde?" es la otra mitad).
 *
 * Va en un módulo aparte y puro porque el valor está en las decisiones de qué
 * NO mostrar, y esas se testean con una tabla de casos, sin renderizar nada.
 *
 * Sobre la seguridad: lo que hay en esas dos columnas **ya está redactado**.
 * `audit.events.redactar()` pasa el diff por el mismo redactor que protege los
 * logs ANTES de escribirlo, así que un secreto nunca llegó a la columna. Esto
 * no destapa nada: muestra lo que ya estaba guardado limpio.
 */

/** Un campo que cambió. Los valores viajan crudos; formatear es del que dibuja. */
export interface ActivityChange {
  field: string
  before: unknown
  after: unknown
}

/**
 * Campos que NUNCA se muestran, y por qué cada uno.
 *
 * No es una lista de conveniencia: cada entrada es ruido que taparía la señal.
 *
 * * `priority_rank` — una clave LexoRank (`0|hzzzzz:`). Cambia en CADA arrastre
 *   de prioridad, que es de lo que más se hace, y "0|hzzzzz → 0|i00007" no le
 *   dice nada a nadie. Si se mostrara, la tab sería una lista de eso.
 * * `updated_at` — cambia en todos los updates por definición. Decir "se
 *   actualizó cuando se actualizó" es una tautología con fecha.
 * * `search_vector` / `tsv` — columnas derivadas que mantiene la base.
 * * `stage_set_id` — acompaña a `stage_id` cuando una empresa clona su set. El
 *   que importa es el stage; el set es plomería.
 */
const OCULTOS = new Set([
  'priority_rank',
  'updated_at',
  'created_at',
  'search_vector',
  'tsv',
  'stage_set_id',
])

/**
 * Campos de texto largo. Se anuncia que cambiaron, **no se vuelca el contenido**.
 *
 * Un brief son párrafos: meterlo entero en una fila de la actividad convierte
 * la lista en un documento. Quien quiera leerlo abre el documento.
 */
export const TEXTO_LARGO = new Set([
  'brief_md',
  'client_summary_md',
  'internal_notes_md',
  'description_md',
  'content_md',
  'notes_internal',
])

/**
 * Qué cambió entre `before` y `after`.
 *
 * Devuelve `[]` cuando `before` es `null`: eso es un ALTA, y un alta no tiene
 * "de qué a qué" — listar los veinte campos iniciales como si fueran cambios
 * sería confundir crear con modificar.
 *
 * La comparación es por JSON y no por `===`: los valores vienen de una columna
 * JSONB y un array o un objeto nunca serían iguales por identidad, así que
 * `===` reportaría como cambiado todo lo que sea estructura.
 */
export function diffDeAuditoria(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
): ActivityChange[] {
  if (!before || !after) return []

  return Object.keys(after)
    .filter((field) => !OCULTOS.has(field))
    .map((field) => ({ field, before: before[field], after: after[field] }))
    .filter((cambio) => !sonIguales(cambio.before, cambio.after))
}

function sonIguales(a: unknown, b: unknown): boolean {
  if (a === b) return true
  // `undefined` y `null` son lo mismo acá: los dos significan "no había valor",
  // y distinguirlos mostraría un cambio donde no hubo ninguno.
  if (a == null && b == null) return true
  return JSON.stringify(a) === JSON.stringify(b)
}
